import { useCallback, useSyncExternalStore } from 'react';

import { AMOUNT_UNITS, DEFAULT_AMOUNT_UNIT, type AmountUnit } from '@/pages/sites/pmis/office/officeDraft';

/**
 * 금액 눈금 — <b>화면 하나가 아니라 사람 하나에 붙는다</b> (사용자 지시 2026-09-14).
 *
 * <h2>왜 화면마다 들고 있으면 안 되나</h2>
 * 종전에는 눈금이 화면마다 따로였다. 원가관리 탭은 억원(기본), 전체 대시보드도 억원, 현장 조회
 * 개요와 현장 수정 모달은 <b>백만원 고정</b>. 그래서 같은 현장의 같은 계약금액이 한쪽에서는
 * {@code 1,083.72}, 다른 쪽에서는 {@code 108,371.98} 로 보였고, 두 화면을 오가던 사람에게는
 * <b>「금액이 다르다」</b>가 됐다(사용자 지적 2026-09-14).
 *
 * <p>한동안 반대로 <b>계약금액만 백만원으로 못박아</b> 풀었는데, 그것은 눈금을 하나로 만드는
 * 대신 <b>토글이 안 듣는 칸</b>을 만드는 일이었다. 사용자가 고른 답은 이쪽이다 —
 * <b>눈금은 하나고, 어느 화면에서 바꾸든 전부 따라온다.</b>
 *
 * <h2>왜 {@code useStoredState} 가 아닌가</h2>
 * 그 훅은 값을 {@code useState} 로 들고 있어 <b>부르는 곳마다 사본이 생긴다</b>. 같은 열쇠를 써도
 * 한 화면에서 바꾼 것이 지금 떠 있는 다른 화면에 닿지 않는다 — 원가관리 탭의 줄과 그 아래 카드가
 * 서로 다른 눈금을 말하게 된다. 여기서는 값을 <b>모듈 하나가 들고</b>, 보고 있는 모든 곳이
 * 구독한다({@code useSyncExternalStore}).
 *
 * <p>다른 <b>탭(창)</b>에서 바꾼 것도 받는다({@code storage} 이벤트) — 대시보드를 한쪽 창에 띄워
 * 두고 다른 창에서 현장을 고치는 것이 실제로 흔한 쓰임이다.
 */
const STORAGE_KEY = 'office.amountUnit';

let current: AmountUnit = read();

const listeners = new Set<() => void>();

function read(): AmountUnit {
  try {
    const stored = Number(window.localStorage.getItem(STORAGE_KEY));
    // 저장된 글자가 우리가 아는 눈금일 때만 받는다 — 손댄 값이나 옛 값이면 기본으로 돌아간다.
    return AMOUNT_UNITS.find((unit) => unit === stored) ?? DEFAULT_AMOUNT_UNIT;
  } catch {
    // 사생활 보호 모드 등에서 접근이 막힐 수 있다. 기억을 못 할 뿐이므로 화면은 그대로 뜬다.
    return DEFAULT_AMOUNT_UNIT;
  }
}

function emit() {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  // 다른 창에서 바뀐 것. `storage` 는 <b>자기 창에서는 안 뜬다</b> — 그쪽은 set 이 직접 알린다.
  const onStorage = (event: StorageEvent) => {
    if (event.key !== STORAGE_KEY) return;
    current = read();
    emit();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

/**
 * 금액 눈금과 그것을 바꾸는 손.
 *
 * <p>돌려주는 값은 <b>모든 화면이 공유한다</b>. 매출·원가·수금도, 도급계약금액도 같은 눈금으로
 * 선다 — 한 화면 안에서 칸마다 눈금이 다르면 그 표는 더할 수 없는 표가 된다.
 */
export function useAmountUnit(): [AmountUnit, (next: AmountUnit) => void] {
  const unit = useSyncExternalStore(subscribe, () => current, () => DEFAULT_AMOUNT_UNIT);

  const set = useCallback((next: AmountUnit) => {
    if (next === current) return;
    current = next;
    try {
      window.localStorage.setItem(STORAGE_KEY, String(next));
    } catch {
      // 기억만 못 한다. 이번 세션 동안은 그대로 듣는다.
    }
    emit();
  }, []);

  return [unit, set];
}
